import { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Button,
    Tooltip,
    Chip
} from '@mui/material';
import { useMapData } from '../../hooks/map/useMapData';
import { useMapActions } from '../../hooks/map/useMapActions';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import TimelineIcon from '@mui/icons-material/Timeline';
import ShapeLineIcon from '@mui/icons-material/ShapeLine';
import SouthIcon from '@mui/icons-material/South';
import RadarIcon from '@mui/icons-material/Radar';
import TripPointsPanel from '../TripPointsPanel';
import { formatHora } from '../../utils/dateUtils';
import { MAP_SYMBOLS } from '../../utils/map/constants';

export default function MapPontosPanel() {
    
    const {
        line,
        selectedPoint,
        route,
        pontos,
        modeMap,
        pezinho,
        lastPosition
    } = useMapData();

    const { pointClick, focusVehicle } = useMapActions();

    const [tab, setTab] = useState(0);

    const pointRefs = useRef({});

    useEffect(() => {

        if (!selectedPoint) {
            return;
        }

        const elem =
            pointRefs.current[
                selectedPoint.PONTO_UID
            ];

        if (!elem) {
            return;
        }

        elem.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });

    }, [selectedPoint]);
    

    return (
        <Box
            sx={(theme) => ({
                width: '100%',
                height: '100%',

                backgroundColor:
                    theme.palette.background.paper,

                display: 'flex',
                flexDirection: 'column',

                overflow: 'hidden',

                pointerEvents:
                    pezinho.loading
                        ? 'none'
                        : 'auto',

                opacity:
                    pezinho.loading
                        ? 0.7
                        : 1,

                transition: 'opacity 0.15s ease',
            })}
        >

            {/* ======================================== */}
            {/* HEADER */}
            {/* ======================================== */}

            <Box 
                sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 1, width: '100%',
                    paddingX: 2, 
                    paddingY: 1,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText
                })}
            >
                <Tooltip title={`Linha de ${line?.SENTIDO === 0 ? 'Entrada' : 'Retorno'}`}>
                    <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                        {line?.SENTIDO === 1 &&
                            <ArrowCircleLeftIcon sx={{ fontSize: 20, marginTop: -1.5 }}/>
                        }                    
                        {line?.SENTIDO === 0 &&
                            <ArrowCircleRightIcon sx={{ fontSize: 20, marginTop: -1.5 }}/>
                        }
                        <TimelineIcon sx={{ fontSize: 20, marginLeft: - 2.5, marginTop: 1.5 }}/>
                    </Box>
                </Tooltip>
                <Typography
                    variant='subtitle1'
                    sx={{ whiteSpace: 'pre-line' }}
                >
                    {`${line?.NOMELINHA} - ${line?.PREFIXO}`}
                </Typography>
                {lastPosition &&
                
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mx: 'auto', cursor: 'pointer'}}
                        onClick={(e) => {
                            e.stopPropagation();
                            focusVehicle();
                        }}
                    >
                        <Tooltip title='Última Posição'>
                            <Typography
                                variant='h6'
                                sx={{ whiteSpace: 'pre-line' }}
                            >
                                <b>Última Posição: {lastPosition.placa} - {formatHora(lastPosition.dataHora, 'DD/MM/YYYY HH:mm:ss')}</b>
                            </Typography>
                        </Tooltip>
                    </Box>
                    
                }
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, ml: 'auto'}}>
                    <Tooltip title='Código de Integração'>
                    <ShapeLineIcon sx={{ fontSize: 20 }}/>
                    </Tooltip>
                    <Typography variant='subtitle1'>
                    {line?.CODIGO_INTEGRACAO ?? ' - '}
                    </Typography>
                </Box>
            </Box>

            {/* ======================================== */}
            {/* TABS */}
            {/* ======================================== */}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: 40
                }}
            >
                <Tabs
                    value={tab}
                    onChange={(e, value) =>
                        setTab(value)
                    }
                    sx={{
                        minHeight: 40
                    }}
                >
                    <Tab
                        label="Pontos"
                        sx={{
                            minHeight: 40
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    />

                    <Tab
                        label="Descrição"
                        sx={{
                            minHeight: 40
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    />
                </Tabs>

                {pezinho.active && (
                    <Chip
                        icon={
                            selectedPoint
                                ? <RadarIcon />
                                : <SouthIcon />
                        }
                        label={
                            selectedPoint
                                ? `Vendo veículos próximos ao ponto ${
                                    selectedPoint?.pontoReferencia?.NOME ?? ''
                                }`
                                : 'Selecione um ponto para buscar veículos'
                        }
                        size="small"
                        color={
                            selectedPoint
                                ? 'success'
                                : 'warning'
                        }
                        variant="outlined"
                        sx={{
                            ml: 2,
                            fontWeight: 500
                        }}
                    />
                )}
            </Box>

            {/* ======================================== */}
            {/* CONTENT */}
            {/* ======================================== */}

            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: modeMap === 'line' || tab === 1 ? 2 : 0
                }}
            >

                {/* ======================================== */}
                {/* PONTOS */}
                {/* ======================================== */}

                {tab === 0 && modeMap === 'line' && (

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            flexDirection: 'column',
                            gap: 2,
                            alignItems: 'flex-start'
                        }}
                    >

                        {pontos?.map((ponto, index) => {
                        
                            const isSelected =
                                selectedPoint?.PONTO_UID ===
                                ponto.PONTO_UID;

                            const isFirst = index === 0;

                            const isLast = index === pontos.length - 1;

                            const pontoReferencia = ponto?.pontoReferencia || null;

                            if(!pontoReferencia)
                                return null;

                            return (
                                <Button
                                    ref={(el) => {

                                        if (!el) {
                                            return;
                                        }

                                        pointRefs.current[
                                            ponto.PONTO_UID
                                        ] = el;
                                    }}
                                    key={`${ponto.PONTO_REFERENCIA_ID}-${index}`}

                                    variant={
                                        isSelected
                                            ? 'contained'
                                            : 'outlined'
                                    }

                                    startIcon={
                                        isFirst ? (

                                            <Box
                                                sx={{
                                                    fontSize: 22,
                                                    lineHeight: 1
                                                }}
                                            >
                                                {MAP_SYMBOLS.startPoint}
                                            </Box>

                                        ) : isLast ? (

                                            <Box
                                                sx={{
                                                    fontSize: 22,
                                                    lineHeight: 1
                                                }}
                                            >
                                                {MAP_SYMBOLS.endPoint}
                                            </Box>

                                        ) : (

                                            <Box
                                                sx={{
                                                    fontSize: 18,
                                                    lineHeight: 1
                                                }}
                                            >
                                                {MAP_SYMBOLS.point}
                                            </Box>

                                        )
                                    }

                                    sx={{
                                        textTransform: 'none',
                                        justifyContent: 'flex-start',
                                        padding: 1
                                    }}

                                    onClick={(e) => {
                                        e.stopPropagation();
                                        pointClick(ponto);
                                    }}
                                >

                                    {isFirst &&
                                        'Início: '
                                    }

                                    {isLast &&
                                        'Fim: '
                                    }

                                    {pontoReferencia?.NOME}

                                </Button>
                            );
                        })}

                    </Box>
                )}

                {tab === 0 && modeMap === 'trip' &&(
                    <TripPointsPanel/>
                )}

                {/* ======================================== */}
                {/* DESCRIÇÃO */}
                {/* ======================================== */}


                {tab === 1 && (

                    <Typography
                        sx={{
                        whiteSpace: 'pre-line'
                        }}
                    >
                        {route ?? 'Sem descrição.'}
                    </Typography>

                )}

            </Box>

        </Box>
    );
}